import { DateTime } from 'luxon'
import {
  type AccountData,
  MINUTE,
  WORKFLOW_PROSPECT_STAGE_ID,
  distanceInMeters,
  getWorkflowAccountTimeline,
  isDefined,
  isEmpty,
  isNotEmpty,
  isNotDefined,
  type LatLng,
  type UndoActionPreferences,
  type UserProfileData,
  type WorkflowHistoryItem,
} from '@terros/common'
import { groupTimelineHistoryItems, isSameHistoryBatch } from './history'

type ValidateUndoLastActionArgs = {
  undoPreferences: UndoActionPreferences
  lastActivity: WorkflowHistoryItem
  propertyLocation: LatLng | undefined
  currentUser: Pick<UserProfileData, 'userId'>
  currentLocation: LatLng
}

type ValidateUndoLastActionResult =
  | {
      error: string
    }
  | undefined

export const validateUndoLastAction = ({
  undoPreferences,
  lastActivity,
  propertyLocation,
  currentUser,
  currentLocation,
}: ValidateUndoLastActionArgs): ValidateUndoLastActionResult => {
  const { userId } = currentUser
  if (isNotDefined(lastActivity)) return { error: 'No activity to undo.' }
  if (lastActivity.userId !== userId || isNotDefined(lastActivity.actionId))
    return { error: 'You cannot undo the last action done by someone else.' }

  const { timeSince, distanceFromLocation } = undoPreferences

  const timeSinceLastActivity = DateTime.now().toMillis() - lastActivity.timestamp
  if (isDefined(timeSince) && timeSince > 0 && timeSinceLastActivity / MINUTE > timeSince)
    return { error: 'You cannot undo this action because it was done before the time limit.' }

  if (isDefined(propertyLocation)) {
    const distance = distanceInMeters(currentLocation, propertyLocation)

    if (isDefined(distanceFromLocation) && distanceFromLocation > 0 && distance > distanceFromLocation)
      return { error: 'You cannot undo this action because you are too far from the property.' }
  }
}

type ShouldPromptDeleteOnUndoArgs = {
  updatedAccount: AccountData
  hasDeletePermission: boolean
  deleteOnUndoEnabled: boolean
}

export const shouldPromptDeleteOnUndo = ({
  updatedAccount,
  hasDeletePermission,
  deleteOnUndoEnabled,
}: ShouldPromptDeleteOnUndoArgs): boolean => {
  if (!deleteOnUndoEnabled) return false
  if (isNotEmpty(updatedAccount.workflowHistory)) return false
  const stageId = updatedAccount.workflowStageId
  if (isDefined(stageId) && stageId !== WORKFLOW_PROSPECT_STAGE_ID) return false
  if (isDefined(updatedAccount.importId)) return false
  if (!hasDeletePermission) return false
  return true
}

export const removeActionHistory = (items: WorkflowHistoryItem[] | undefined): WorkflowHistoryItem[] => {
  if (isEmpty(items)) return []
  const timeline = getWorkflowAccountTimeline({ workflowHistory: items })
  const groupedTimeline = groupTimelineHistoryItems(timeline)
  const lastGroup = groupedTimeline.at(-1)
  if (isNotDefined(lastGroup)) return timeline

  const spliceIndex = lastGroup.at(0)?.originalTimelineIndex
  if (isNotDefined(spliceIndex)) return timeline
  return timeline.slice(0, getUndoBatchStartIndex(timeline, spliceIndex)).sort((a, b) => b.timestamp - a.timestamp)
}

function getUndoBatchStartIndex(timeline: WorkflowHistoryItem[], index: number): number {
  let start = index
  while (start > 0 && isSameHistoryBatch(timeline[start - 1], timeline[start])) {
    start -= 1
  }
  return start
}
